/**
 * OTA Service - Admin Dashboard
 * Principios SOLID aplicados:
 * - SRP: Responsabilidad única de gestionar actualizaciones OTA
 * - DIP: Usa ApiService en lugar de fetch directo
 * - OCP: Extensible mediante nuevos métodos sin modificar existentes
 */

import { adminApi } from '../services/ApiService';
import { OTA_CONFIG } from '../constants/config';

/**
 * Obtener información completa de firmware para un dispositivo
 * @param {string} deviceMAC - MAC address del dispositivo
 * @param {string} model - Modelo del dispositivo (opcional)
 * @returns {Promise<object>} - Información del firmware
 */
export async function getFirmwareInfo(deviceMAC, model = null) {
  try {
    // Combinar info del dispositivo + versiones de GitHub
    const [deviceData, githubData] = await Promise.allSettled([
      adminApi.get(`/admin/devices/${encodeURIComponent(deviceMAC)}`),
      adminApi.get('/admin/firmware/github')
    ]);

    const device = deviceData.status === 'fulfilled' ? deviceData.value : null;
    const github = githubData.status === 'fulfilled' ? githubData.value : null;

    // Extraer version actual del dispositivo
    let currentVersion = 'Desconocida';
    if (device?.status) {
      try {
        const st = typeof device.status === 'string' ? JSON.parse(device.status) : device.status;
        currentVersion = st?.data?.device?.firmware_version || st?.device?.firmware_version || st?.firmware_version || currentVersion;
      } catch {}
    }

    const deviceModel = model || device?.model || 'GP-BOX';
    const latestVersions = github?.versions?.[deviceModel] || github?.versions || {};

    return {
      success: true,
      device: { mac: deviceMAC, model: deviceModel, currentVersion },
      latestVersions
    };
  } catch (error) {
    console.error('Error obteniendo información de firmware:', error);
    return {
      success: false,
      message: error.message
    };
  }
}

/**
 * Obtener todas las versiones disponibles de firmware
 * @returns {Promise<object>} - Versiones disponibles por modelo
 */
export async function getAllFirmwareVersions() {
  try {
    const data = await adminApi.get('/admin/firmware/github');

    if (data.success && data.versions) {
      return data.versions;
    } else {
      return {};
    }
  } catch (error) {
    console.error('Error obteniendo versiones:', error);
    return {};
  }
}

/**
 * Disparar actualización OTA para un dispositivo
 * @param {string} deviceMAC - MAC address del dispositivo
 * @param {string|null} firmwareUrl - URL del firmware (opcional, el servidor la construirá automáticamente)
 * @returns {Promise<object>} - Resultado de la actualización
 */
export async function triggerOTAUpdate(deviceMAC, firmwareUrl = null) {
  try {
    const payload = { mac: deviceMAC };

    // Solo incluir firmwareUrl si se provee (para casos especiales)
    if (firmwareUrl) {
      payload.firmwareUrl = firmwareUrl;
    }

    const data = await adminApi.post('/admin/commands/ota', payload);

    return data;
  } catch (error) {
    console.error('Error disparando actualización:', error);
    return {
      success: false,
      message: error.message
    };
  }
}

/**
 * Disparar actualización OTA masiva para múltiples dispositivos
 * @param {Array<string>} deviceMACs - Array de MAC addresses
 * @param {number} delayBetweenUpdates - Delay en ms entre cada actualización
 * @param {Function} progressCallback - Callback para reportar progreso (current, total)
 * @returns {Promise<object>} - Resultados de todas las actualizaciones
 */
export async function triggerBulkOTAUpdate(
  deviceMACs, 
  delayBetweenUpdates = OTA_CONFIG.BULK_UPDATE_DELAY,
  progressCallback = null
) {
  const results = {
    total: deviceMACs.length,
    successful: 0,
    failed: 0,
    details: []
  };

  for (let i = 0; i < deviceMACs.length; i++) {
    const mac = deviceMACs[i];
    
    try {
      // Reportar progreso
      if (progressCallback) {
        progressCallback(i + 1, deviceMACs.length);
      }
      
      const result = await triggerOTAUpdate(mac);
      
      if (result.success) {
        results.successful++;
        results.details.push({ mac, success: true });
      } else {
        results.failed++;
        results.details.push({ mac, success: false, error: result.message });
      }

      // Esperar antes del siguiente dispositivo (excepto el último)
      if (i < deviceMACs.length - 1) {
        await new Promise(resolve => setTimeout(resolve, delayBetweenUpdates));
      }
    } catch (error) {
      console.error(`Error actualizando ${mac}:`, error);
      results.failed++;
      results.details.push({ mac, success: false, error: error.message });
    }
  }

  return results;
}

/**
 * Verificar si hay actualización disponible comparando versiones
 * @param {string} currentVersion - Versión actual del dispositivo
 * @param {string} latestVersion - Última versión disponible
 * @param {string} buildDate - Fecha de compilación
 * @param {string} buildTime - Hora de compilación
 * @param {string} releaseDate - Fecha del release
 * @returns {boolean|null} - true si hay actualización, false si no, null si no se puede determinar
 */
export function isUpdateAvailable(currentVersion, latestVersion, buildDate = null, buildTime = null, releaseDate = null) {
  // Comparar versiones semánticas
  if (currentVersion && latestVersion) {
    const comparison = compareVersions(currentVersion, latestVersion);
    if (comparison !== null && comparison < 0) {
      return true;
    }
    if (comparison !== null && comparison >= 0) {
      return false;
    }
  }

  // Comparar por fecha si no hay versiones válidas
  if (buildDate && releaseDate) {
    return compareByDate(buildDate, buildTime, releaseDate);
  }

  return null;
}

/**
 * Comparar versiones semánticas
 */
function compareVersions(v1, v2) {
  if (!v1 || !v2) return null;

  const semverRegex = /^\d+\.\d+(\.\d+)?$/;
  if (!semverRegex.test(v1) || !semverRegex.test(v2)) {
    return null;
  }

  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;

    if (p1 > p2) return 1;
    if (p1 < p2) return -1;
  }

  return 0;
}

/**
 * Comparar por fecha de compilación
 */
function compareByDate(buildDate, buildTime, releaseDate) {
  try {
    const buildDateTimeStr = `${buildDate} ${buildTime || '00:00:00'}`;
    const buildDateTime = new Date(buildDateTimeStr);
    const releaseDateTime = new Date(releaseDate);

    return releaseDateTime > buildDateTime;
  } catch (error) {
    console.error('Error comparando fechas:', error);
    return null;
  }
}

export default {
  getFirmwareInfo,
  getAllFirmwareVersions,
  triggerOTAUpdate,
  triggerBulkOTAUpdate,
  isUpdateAvailable
};
